import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { LabelOptions } from 'chartjs-plugin-datalabels/types/options'
import { Bar } from 'react-chartjs-2'
import { useWindowSize } from 'usehooks-ts'

import { Database_Procs_KASPercentAggregatedByLevel_Item } from '@suntex.fim/api-client'
import { $KnowAndShowCategory } from '@suntex.fim/enums'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
)
// Constants
const MOBILE_BREAKPOINT = 745
const DEFAULT_FONT_SIZE = 14
const MOBILE_FONT_SIZE = 8
const LEVEL_THRESHOLD = 2
const BASIC_LEVEL_LABELS_COUNT = 4
const allLabels = $KnowAndShowCategory.describeAll().map((item) => item.label)

// Helper function to get responsive font size
const getResponsiveFontSize = (
  windowWidth: number,
  defaultSize: number = DEFAULT_FONT_SIZE,
  mobileSize: number = MOBILE_FONT_SIZE
) => {
  return windowWidth <= MOBILE_BREAKPOINT ? mobileSize : defaultSize
}

/**
 * returns JSX for know and show chart
 */
function AssessmentKSCharts({
  backgroundColor,
  level,
  chartData
}: {
  backgroundColor: string
  level: number
  chartData: Partial<Database_Procs_KASPercentAggregatedByLevel_Item>
}) {
  const { width = 1024 } = useWindowSize()

  const getOptions = () => ({
    responsive: true,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: 'black',
        formatter: (value: string) => {
          return `${value || 0}%\n` // patch:bottom margin not applicable so added with lines
        },
        font: {
          size: getResponsiveFontSize(width),
          weight: 700
        },
        anchor: 'end'
      } as LabelOptions,
      tooltip: {
        borderColor: '#D2D2D2',
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (context: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const label = context.raw || 0
            return ` ${label} %`
          },
          title: () => {
            return ''
          }
        },
        bodyAlign: 'center',
        backgroundColor: 'white',
        titleColor: 'black',
        bodyColor: 'black',
        bodyFont: {
          size: DEFAULT_FONT_SIZE,
          weight: 700
        }
      }
    },
    maintainAspectRatio: false,
    layout: { autoPadding: true, padding: { top: 20 } },
    scales: {
      x: {
        ticks: {
          font: {
            size: getResponsiveFontSize(width),
            weight: 700
          }
        }
      },
      y: {
        min: 0,
        max: 100,
        // Show all major tick values
        major: {
          enabled: true
        },
        ticks: {
          stepSize: 20, // Adjust step size to ensure 10, 50, and 100 are displayed
          callback: (value: number | string) => `${value} %`
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
      axis: 'x'
    }
  })
  const labels =
    level > LEVEL_THRESHOLD
      ? [...allLabels]
      : [...allLabels].splice(0, BASIC_LEVEL_LABELS_COUNT)
  const data: ChartData<'bar', number[], string> = {
    labels,
    datasets: [
      {
        data: labels.map(
          (_label, index) =>
            chartData[
              `ks_${level}_${
                index + 1
              }` as keyof Database_Procs_KASPercentAggregatedByLevel_Item &
                `ks_${string}`
            ] || 0
        ),
        backgroundColor,
        borderRadius: 6,
        borderSkipped: false
      }
    ]
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
  return <Bar options={getOptions() as any} data={data} />
}

export default AssessmentKSCharts
